// components/ReciprocatingCompressorCalculator.js
//
// Reciprocating compressor performance simulator
// Physics basis:
//   - Volumetric efficiency:  η_v = 1 + c - c·r^(1/n)
//       c = clearance fraction (e.g. 0.08), r = P2/P1, n = polytropic index ≈ γ
//   - Displacement rate:   V_d = (π/4)·bore²·stroke·RPM/60 × n_cyl  [m³/s]
//   - Actual inlet flow:   Q_act = V_d · η_v  [m³/s at inlet conditions]
//   - Mass flow:           ṁ = Q_act · ρ_inlet  [kg/s]
//   - Isentropic discharge T:  T2s = T1·r^((γ-1)/γ)
//   - Actual discharge T:  T2 = T1 + (T2s-T1)/η_is
//   - Isentropic specific work:  w_is = Cp_mix·(T2s-T1)·Z1  [kJ/kg]
//   - Brake power per stage: W = ṁ·w_is/η_is  [kW]
//   - Inter-stage cooler: reduces T back to T_cooler, Z-recalculated
//   - Liquid dropout at cooler: Wilson K-value flash
//   - Standard flow: Q_std = ṁ/ρ_std converted to e³m³/d
//
// PREVIOUS BUG (from session history):
//   η_v was computed without Z-correction and with gamma from pure methane.
//   This has been fixed here: gamma uses mixture Cp (mole-weighted) and real-gas Cv.

import React, { useState, useEffect, useCallback } from 'react';
import { COMPONENTS, R, pengRobinsonZ, calculateEntropy } from '../fluid-properties';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, ZAxis } from 'recharts';

// ─── Constants ──────────────────────────────────────────────────────────────
const STD_T_K = 288.15; // 15°C standard temperature
const STD_P_bar = 1.01325; // 1 atm standard pressure

// ─── Wilson K-value flash (Rachford-Rice) ───────────────────────────────────
// Used to determine liquid dropout after intercoolers

function wilsonK(Tc_K, Pc_bar, omega, T_K, P_bar) {
  // Wilson (1969) correlation
  return (Pc_bar / P_bar) * Math.exp(5.373 * (1 + omega) * (1 - Tc_K / T_K));
}

function rachfordRice(Ki, zi) {
  // Solve Σ zi(Ki-1)/(1+β(Ki-1)) = 0  for β (vapour fraction) — bisection
  let lo = 1 / (1 - Math.max(...Ki)) + 1e-8;
  let hi = 1 / (1 - Math.min(...Ki)) - 1e-8;
  lo = Math.max(lo, 0);
  hi = Math.min(hi, 1);
  if (lo >= hi) return lo > 0.5 ? 1.0 : 0.0;
  for (let iter = 0; iter < 100; iter++) {
    const beta = (lo + hi) / 2;
    const f = zi.reduce((s, z, i) => s + z * (Ki[i] - 1) / (1 + beta * (Ki[i] - 1)), 0);
    if (Math.abs(f) < 1e-8) return beta;
    f > 0 ? (lo = beta) : (hi = beta);
  }
  return (lo + hi) / 2;
}

function flashCalc(compArray, zi, T_K, P_bar) {
  // Returns { beta (vapour frac), yi (vapour comp), xi (liquid comp) }
  const Ki = compArray.map(c => wilsonK(c.Tc, c.Pc, c.omega, T_K, P_bar));

  // Trivial check — all vapour if all K > 1, all liquid if all K < 1
  if (Ki.every(k => k >= 1)) return { beta: 1.0, yi: [...zi], xi: [...zi] };
  if (Ki.every(k => k <= 1)) return { beta: 0.0, yi: [...zi], xi: [...zi] };

  const beta = rachfordRice(Ki, zi);
  const xi = zi.map((z, i) => z / (1 + beta * (Ki[i] - 1)));
  const yi = xi.map((x, i) => x * Ki[i]);
  // Normalise
  const sumX = xi.reduce((s, x) => s + x, 0);
  const sumY = yi.reduce((s, y) => s + y, 0);
  return {
    beta,
    xi: xi.map(x => x / sumX),
    yi: yi.map(y => y / sumY),
  };
}

// ─── Mixture properties from an array of {comp, xi} pairs ──────────────────

function mixProps(compArray, zi, T_K, P_bar) {
  // Mole-weighted pseudocritical properties (Kay's mixing rules)
  let MW = 0, Tpc = 0, Ppc = 0, omega_mix = 0;
  compArray.forEach((c, i) => {
    MW        += zi[i] * c.MW;
    Tpc       += zi[i] * c.Tc;
    Ppc       += zi[i] * c.Pc;
    omega_mix += zi[i] * c.omega;
  });

  // Z-factor: use the validated Peng-Robinson solver from fluid-properties.js
  // pengRobinsonZ(T_K, P_bar, Tc, Pc, omega) → { Z, a, b, alpha, kappa, A, B, Tc, allRoots }
  const prResult = pengRobinsonZ(T_K, P_bar, Tpc, Ppc, omega_mix);
  const Z = prResult.Z;

  // Mixture Cp — mole-weighted ideal gas at T_K  [J/(mol·K)]
  let Cp_mix = 0;
  compArray.forEach((c, i) => {
    const [A, B, C, D] = c.cp;
    Cp_mix += zi[i] * (A + B*T_K + C*T_K**2 + D*T_K**3);
  });

  // Real-gas γ = Cp / (Cp − R·Z)   [Bug fix from session 1]
  const Cv_real = Cp_mix - R.J_MOL_K * Z;
  const gamma = Cp_mix / Math.max(Cv_real, R.J_MOL_K * 0.5);

  // Mass-specific Cp  [kJ/(kg·K)]  — J/(mol·K) ÷ (g/mol) = J/g·K = kJ/kg·K
  const Cp_mass = Cp_mix / MW;

  // Molar density at (T,P) → mass density  [kg/m³]
  const rho = (P_bar * 1e5 * MW * 1e-3) / (Z * R.J_MOL_K * T_K);

  return { MW, Tpc, Ppc, Z, gamma, Cp_mass, rho, Cp_mix };
}

// ─── Single-stage compression ───────────────────────────────────────────────

function compressStage(compArray, zi, T1_K, P1_bar, P2_bar,
                       bore_mm, stroke_mm, cylinders, clearance_pct,
                       rpm, eta_is, eta_mech) {
  const r = P2_bar / P1_bar; // compression ratio

  const { Z: Z1, gamma, Cp_mass, rho, MW } = mixProps(compArray, zi, T1_K, P1_bar);

  // ── Volumetric efficiency (CORRECTLY accounting for Z) ──────────────────
  // η_v = 1 + c - c·(P2/P1·Z1/Z2)^(1/n)
  // where Z2 needs T2 which we don't know yet — iterate or approximate Z2 ≈ Z1 for η_v
  // (Z ratio correction: API 618 method)
  const c = clearance_pct / 100;
  // Approx Z2 from isentropic T2
  const T2s_K = T1_K * r ** ((gamma - 1) / gamma);
  const { Z: Z2s } = mixProps(compArray, zi, T2s_K, P2_bar);
  // API 618 volumetric efficiency with Z ratio: η_v = 1 + c - c·(r·Z1/Z2)^(1/γ)
  const eta_v = 1 + c - c * Math.pow(r * Z1 / Z2s, 1 / gamma);
  const eta_v_clamped = Math.max(0.05, Math.min(0.99, eta_v));

  // ── Piston displacement [m³/s] ───────────────────────────────────────────
  const bore_m   = bore_mm / 1000;
  const stroke_m = stroke_mm / 1000;
  const Vd = (Math.PI / 4) * bore_m ** 2 * stroke_m * (rpm / 60) * cylinders;

  // ── Actual volumetric flow at inlet conditions [m³/s] ───────────────────
  const Q_act = Vd * eta_v_clamped; // m³/s at (T1, P1)

  // ── Mass flow [kg/s] ─────────────────────────────────────────────────────
  const m_dot = Q_act * rho;

  // ── Discharge temperatures ───────────────────────────────────────────────
  // Isentropic
  // T2s already calculated above; recalculate from actual T1
  const T2s_actual = T1_K * r ** ((gamma - 1) / gamma);
  // Actual (accounting for isentropic efficiency)
  const T2_actual = T1_K + (T2s_actual - T1_K) / eta_is;

  // ── Isentropic work per unit mass [kJ/kg] ────────────────────────────────
  // w_is = Cp_mass · (T2s - T1) · Z1   [Z1 corrects for real-gas departure]
  // This is the Bug 3 fix: multiply by Z1
  const w_is = Cp_mass * (T2s_actual - T1_K) * Z1; // kJ/kg

  // ── Stage power [kW] ─────────────────────────────────────────────────────
  const W_stage = m_dot * w_is / eta_is / eta_mech; // kW

  // ── Standard volumetric flow [m³/s std, then e³m³/d] ────────────────────
  const Z_std  = 1.0; // ideal gas at standard conditions
  const rho_std = (STD_P_bar * 1e5 * MW * 1e-3) / (Z_std * R.J_MOL_K * STD_T_K);
  const Q_std_m3s   = m_dot / rho_std;                     // m³/s at STP
  const Q_e3m3d     = Q_std_m3s * 86400 / 1000;            // e³m³/d

  return {
    T1_K, T2s_K: T2s_actual, T2_K: T2_actual,
    P1_bar, P2_bar, r,
    Z1, Z2: Z2s, gamma,
    eta_v: eta_v_clamped, Vd, Q_act, m_dot,
    W_stage, w_is,
    Q_e3m3d, MW,
    Cp_mass,
  };
}

// ─── Full multi-stage calculation at a given RPM ────────────────────────────

function calculateAtRPM(compArray, zi_init, cfg, rpm) {
  const {
    suctionPressure, dischargePressure, suctionTemperature,
    stages, bore, stroke, clearance, cylinders,
    coolerOutletTemperature, eta_is, eta_mech,
  } = cfg;

  // Per-stage pressure ratio (equal for all stages — typical design basis)
  const overallRatio = dischargePressure / suctionPressure;
  const stageRatio   = Math.pow(overallRatio, 1 / stages);

  let P_curr  = suctionPressure;
  let T_curr  = suctionTemperature + 273.15;
  let zi_curr = [...zi_init]; // current vapour-phase composition

  let totalPower   = 0;
  let totalCooling = 0;
  const stageResults = [];

  for (let s = 0; s < stages; s++) {
    const P2 = P_curr * stageRatio;

    const res = compressStage(
      compArray, zi_curr, T_curr, P_curr, P2,
      bore[s], stroke[s], cylinders[s], clearance[s],
      rpm, eta_is, eta_mech,
    );

    // Inter-stage cooling: cool to coolerOutletTemperature
    const T_cooler_K = coolerOutletTemperature + 273.15;
    // Cooling duty = ṁ·Cp·(T2-Tcooler)
    const coolingDuty = res.m_dot * res.Cp_mass * (res.T2_K - T_cooler_K); // kW

    // Flash at cooler outlet to determine liquid dropout and vapour composition
    const flash = flashCalc(compArray, zi_curr, T_cooler_K, P2);
    // Vapour phase continues to next stage
    zi_curr = flash.yi;

    // Vapour composition summary for display
    const vaporComp = {};
    const liquidComp = {};
    compArray.forEach((c, i) => {
      if (flash.yi[i] > 0.0001) vaporComp[c.name] = flash.yi[i];
      if (flash.xi[i] > 0.0001) liquidComp[c.name] = flash.xi[i];
    });

    // Snapshot loop variables before closures so they capture current values, not mutable bindings
    const snap_P_in  = P_curr;
    const snap_T_in  = T_curr;
    const snap_zi    = zi_curr;
    const snap_P2    = P2;
    const snap_T2_K  = res.T2_K;
    const snap_T2s_K = res.T2s_K;

    stageResults.push({
      stageNumber: s + 1,
      inletPressure: snap_P_in,
      inletTemperature: snap_T_in - 273.15,
      dischargePressure: snap_P2,
      dischargeTemperature: snap_T2_K - 273.15,
      isentropicDischargeTemp: snap_T2s_K - 273.15,
      coolerOutletTemperature: coolerOutletTemperature,
      compressionRatio: res.r,
      volumetricEfficiency: res.eta_v * 100,
      Z_inlet: res.Z1, Z_discharge: res.Z2,
      gamma: res.gamma,
      power: res.W_stage,
      coolingDuty: Math.max(0, coolingDuty),
      massFlow: res.m_dot,
      MW: res.MW,
      Q_e3m3d: res.Q_e3m3d,
      Q_act: res.Q_act * 1000,
      displacement: res.Vd * 1000,
      w_is: res.w_is,
      vaporFraction: flash.beta,
      vaporComposition: vaporComp,
      liquidComposition: liquidComp,
      // Curved T-s diagram paths (High Resolution)
      tsPathActual: Array.from({ length: 26 }, (_, i) => {
        const ratio = i / 25;
        const p = snap_P_in * Math.pow(snap_P2 / snap_P_in, ratio);
        const m = Math.log(snap_T2_K / snap_T_in) / Math.log(snap_P2 / snap_P_in);
        const t = snap_T_in * Math.pow(p / snap_P_in, m);
        return { s: calculateEntropy(t, p, compArray, snap_zi), T: t - 273.15 };
      }),
      tsPathIdeal: Array.from({ length: 26 }, (_, i) => {
        const ratio = i / 25;
        const p = snap_P_in * Math.pow(snap_P2 / snap_P_in, ratio);
        const m_is = Math.log(snap_T2s_K / snap_T_in) / Math.log(snap_P2 / snap_P_in);
        const t = snap_T_in * Math.pow(p / snap_P_in, m_is);
        return { s: calculateEntropy(t, p, compArray, snap_zi), T: t - 273.15 };
      }),
      tsPathCooling: Array.from({ length: 26 }, (_, i) => {
        const t = snap_T2_K + (T_cooler_K - snap_T2_K) * (i / 25);
        return { s: calculateEntropy(t, snap_P2, compArray, snap_zi), T: t - 273.15 };
      }),
      // P-V Diagram points (Indicator Card) — captured via IIFE with explicit params
      pvPath: ((Pin, Pout, gam, boreM, strokeM, clearFrac) => {
        const n = gam;
        const Vs = (Math.PI / 4) * boreM ** 2 * strokeM;
        const Vc = clearFrac * Vs;
        const Vtotal = Vs + Vc;
        const Pa = Pin,  Va = Vtotal;
        const Vb = Vtotal * Math.pow(Pin / Pout, 1 / n);
        const Pb = Pout;
        const Vc_pt = Vc, Pc = Pout;
        const Vd = Vc * Math.pow(Pout / Pin, 1 / n);
        const path = [];
        for (let i = 0; i <= 10; i++) {
          const v = Va + (Vb - Va) * (i / 10);
          const p = Pa * Math.pow(Va / v, n);
          path.push({ V: v * 1000, P: p });
        }
        for (let i = 1; i <= 5; i++) {
          const v = Vb + (Vc_pt - Vb) * (i / 5);
          path.push({ V: v * 1000, P: Pb });
        }
        for (let i = 1; i <= 10; i++) {
          const v = Vc_pt + (Vd - Vc_pt) * (i / 10);
          const p = Pc * Math.pow(Vc_pt / v, n);
          path.push({ V: v * 1000, P: p });
        }
        for (let i = 1; i <= 5; i++) {
          const v = Vd + (Va - Vd) * (i / 5);
          path.push({ V: v * 1000, P: Pa });
        }
        return path;
      })(snap_P_in, snap_P2, res.gamma, cfg.bore[s] / 1000, cfg.stroke[s] / 1000, cfg.clearance[s] / 100)
    });

    totalPower   += res.W_stage;
    totalCooling += Math.max(0, coolingDuty);

    // Next stage inlet = cooler outlet, vapour only
    P_curr = P2;
    T_curr = T_cooler_K;
  }

  // Standard flow = stage 1 inlet mass flow ÷ standard density
  // (res.Q_e3m3d was already computed correctly inside compressStage)
  const Q_e3m3d = stageResults[0]?.Q_e3m3d ?? 0;

  return {
    rpm,
    totalPower,
    totalCooling,
    specificPower: Q_e3m3d > 0 ? totalPower / Q_e3m3d : 0,
    gasFlowVolE3m3d: Q_e3m3d,
    stages: stageResults,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// React Component
// ═══════════════════════════════════════════════════════════════════════════

const COMPONENT_LIST = Object.values(COMPONENTS);

const ReciprocatingCompressorCalculator = ({ initialComposition }) => {
  const defaultComp = initialComposition || {
    [COMPONENTS.METHANE.name]: 85.0,
    [COMPONENTS.ETHANE.name]:   8.0,
    [COMPONENTS.PROPANE.name]:  3.0,
    [COMPONENTS.BUTANE.name]:   1.0,
    [COMPONENTS.PENTANE.name]:  0.5,
    [COMPONENTS.HEXANE.name]:   0.2,
    [COMPONENTS.NITROGEN.name]: 1.0,
    [COMPONENTS.CO2.name]:      1.0,
    [COMPONENTS.H2S.name]:      0.1,
    [COMPONENTS.WATER.name]:    0.2,
  };

  const [useMainComp, setUseMainComp] = useState(true);
  const [composition, setComposition] = useState(defaultComp);

  // Update composition if parent feed changes and we're using main page comp
  useEffect(() => {
    if (useMainComp && initialComposition) setComposition(initialComposition);
  }, [initialComposition, useMainComp]);

  const [cfg, setCfg] = useState({
    stages: 2,
    suctionPressure: 20,        // bar
    dischargePressure: 160,     // bar
    suctionTemperature: 30,     // °C
    coolerOutletTemperature: 35,// °C
    eta_is: 0.82,               // isentropic (indicated) efficiency
    eta_mech: 0.96,             // mechanical efficiency
    minRPM: 300,
    maxRPM: 1000,
    rpmStep: 50,
    bore:      [300, 200, 150], // mm per stage
    stroke:    [300, 300, 300],
    cylinders: [1,   1,   1  ],
    clearance: [8,   8,   8  ],
  });

  const [results, setResults] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [midRPMidx, setMidRPMidx] = useState(0);

  // Sync stage-array lengths when stages changes
  useEffect(() => {
    const s = cfg.stages;
    const pad = (arr, def) => {
      const a = [...arr];
      while (a.length < s) a.push(a[a.length-1] ?? def);
      return a.slice(0, s);
    };
    setCfg(prev => ({
      ...prev,
      bore:      pad(prev.bore,      200),
      stroke:    pad(prev.stroke,    300),
      cylinders: pad(prev.cylinders, 1),
      clearance: pad(prev.clearance, 8),
    }));
  }, [cfg.stages]);

  const updateCfg = (key, val) => setCfg(prev => ({ ...prev, [key]: val }));
  const updateArr = (key, idx, val) =>
    setCfg(prev => { const a = [...prev[key]]; a[idx] = +val; return { ...prev, [key]: a }; });

  const runCalculation = useCallback(() => {
    setCalculating(true);
    setTimeout(() => { // let React render "Calculating…" first
      try {
        // Build normalised composition arrays
        const totalMol = Object.values(composition).reduce((s, v) => s + v, 0);
        const compArray = [];
        const zi = [];
        COMPONENT_LIST.forEach(c => {
          const pct = composition[c.name] || 0;
          if (pct > 0) {
            compArray.push(c);
            zi.push(pct / totalMol);
          }
        });

        const rpms = [];
        for (let r = cfg.minRPM; r <= cfg.maxRPM; r += cfg.rpmStep) rpms.push(r);

        const rpmResults = rpms.map(rpm => calculateAtRPM(compArray, zi, cfg, rpm));
        const mid = Math.floor(rpmResults.length / 2);
        setMidRPMidx(mid);
        setResults({ rpmResults });
      } catch (err) {
        console.error('Compressor calc error:', err);
      } finally {
        setCalculating(false);
      }
    }, 10);
  }, [composition, cfg]);

  const midResult = results?.rpmResults[midRPMidx];

  const TOOLTIP_STYLE = { background: '#161b22', border: '1px solid #30363d', color: '#e6edf3', fontSize: 12 };

  return (
    <div className="tool-panel">
      <h3>Reciprocating Compressor Simulator</h3>
      <p style={{color:'#8b9ab3', fontSize:13, marginBottom:16}}>
        Multi-stage recip performance across an RPM sweep. Volumetric efficiency uses API 618
        clearance method with Z-ratio correction. Isentropic work uses real-gas mixture γ (Bug fix applied).
      </p>

      {/* ── Composition source ─────────────────────────────────────────── */}
      <div className="info-box" style={{marginBottom:12}}>
        <label style={{display:'flex', alignItems:'center', gap:10, fontWeight:'normal'}}>
          <input type="checkbox" checked={useMainComp}
            onChange={e => { setUseMainComp(e.target.checked); if(e.target.checked&&initialComposition) setComposition(initialComposition); }} />
          Use feed composition from Plant Energy Model page
        </label>
      </div>

      {!useMainComp && (
        <div style={{marginBottom:16}}>
          <h4>Gas Composition (mol%)</h4>
          <div className="two-col-grid">
            {COMPONENT_LIST.map(c => (
              <div className="input-group" key={c.name}>
                <label>{c.name}</label>
                <input type="number" step="0.1" min="0"
                  value={composition[c.name] ?? 0}
                  onChange={e=>setComposition(prev=>({...prev,[c.name]:+e.target.value}))} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Compressor configuration ───────────────────────────────────── */}
      <h4>Compressor Configuration</h4>
      <div className="two-col-grid">
        <div className="input-group">
          <label>Number of Stages</label>
          <select value={cfg.stages} onChange={e=>updateCfg('stages',+e.target.value)}>
            {[1,2,3,4].map(n=><option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="input-group">
          <label>Suction Pressure (bar)</label>
          <input type="number" step="0.5" value={cfg.suctionPressure}
            onChange={e=>updateCfg('suctionPressure',+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Discharge Pressure (bar)</label>
          <input type="number" step="1" value={cfg.dischargePressure}
            onChange={e=>updateCfg('dischargePressure',+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Suction Temperature (°C)</label>
          <input type="number" value={cfg.suctionTemperature}
            onChange={e=>updateCfg('suctionTemperature',+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Inter-stage Cooler Outlet T (°C)</label>
          <input type="number" value={cfg.coolerOutletTemperature}
            onChange={e=>updateCfg('coolerOutletTemperature',+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Isentropic (Indicated) Efficiency</label>
          <input type="number" step="0.01" min="0.5" max="0.99" value={cfg.eta_is}
            onChange={e=>updateCfg('eta_is',+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Mechanical Efficiency</label>
          <input type="number" step="0.01" min="0.8" max="1.0" value={cfg.eta_mech}
            onChange={e=>updateCfg('eta_mech',+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Min RPM</label>
          <input type="number" step="50" value={cfg.minRPM}
            onChange={e=>updateCfg('minRPM',+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Max RPM</label>
          <input type="number" step="50" value={cfg.maxRPM}
            onChange={e=>updateCfg('maxRPM',+e.target.value)} />
        </div>
        <div className="input-group">
          <label>RPM Step</label>
          <input type="number" step="25" value={cfg.rpmStep}
            onChange={e=>updateCfg('rpmStep',+e.target.value)} />
        </div>
      </div>

      {/* ── Per-stage cylinder geometry ────────────────────────────────── */}
      <h4 style={{marginTop:16}}>Cylinder Geometry (per stage)</h4>
      <div style={{overflowX:'auto'}}>
        <table className="stage-table">
          <thead>
            <tr>
              <th>Stage</th>
              <th>Bore (mm)</th>
              <th>Stroke (mm)</th>
              <th>Cylinders</th>
              <th>Clearance (%)</th>
              <th>P ratio (auto)</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({length: cfg.stages}, (_, s) => {
              const r = Math.pow(cfg.dischargePressure/cfg.suctionPressure, 1/cfg.stages);
              const P1 = cfg.suctionPressure * r**s;
              const P2 = P1 * r;
              return (
                <tr key={s}>
                  <td>{s+1}</td>
                  <td><input type="number" step="10" style={{width:80}}
                    value={cfg.bore[s]??300} onChange={e=>updateArr('bore',s,e.target.value)} /></td>
                  <td><input type="number" step="10" style={{width:80}}
                    value={cfg.stroke[s]??300} onChange={e=>updateArr('stroke',s,e.target.value)} /></td>
                  <td><input type="number" step="1" min="1" style={{width:60}}
                    value={cfg.cylinders[s]??1} onChange={e=>updateArr('cylinders',s,e.target.value)} /></td>
                  <td><input type="number" step="0.5" min="3" max="20" style={{width:70}}
                    value={cfg.clearance[s]??8} onChange={e=>updateArr('clearance',s,e.target.value)} /></td>
                  <td style={{color:'#8b9ab3', fontSize:12}}>{P1.toFixed(1)} → {P2.toFixed(1)} bar ({r.toFixed(2)}:1)</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button className="calculate-button" style={{marginTop:16}}
        onClick={runCalculation} disabled={calculating}>
        {calculating ? 'Calculating…' : '▶ Run RPM Sweep'}
      </button>

      {/* ══ RESULTS ════════════════════════════════════════════════════════ */}
      {results && (
        <div style={{marginTop:24}}>
          <h3>Results</h3>

          {/* Summary cards at mid-RPM */}
          {midResult && (
            <div className="result-cards">
              <div className="result-card highlight">
                <div className="result-label">Flow at {midResult.rpm} RPM</div>
                <div className="result-value">{midResult.gasFlowVolE3m3d.toFixed(1)} e³m³/d</div>
              </div>
              <div className="result-card highlight">
                <div className="result-label">Brake Power at {midResult.rpm} RPM</div>
                <div className="result-value">{midResult.totalPower.toFixed(0)} kW</div>
              </div>
              <div className="result-card">
                <div className="result-label">Specific Power</div>
                <div className="result-value">{midResult.specificPower.toFixed(2)}</div>
                <div className="result-note">kW / (e³m³/d)</div>
              </div>
              <div className="result-card">
                <div className="result-label">Total Cooling Duty</div>
                <div className="result-value">{midResult.totalCooling.toFixed(0)} kW</div>
              </div>
            </div>
          )}

          {/* Performance curves */}
          <h4 style={{marginTop:20}}>Performance Curves</h4>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={results.rpmResults}
              margin={{top:5, right:40, left:10, bottom:20}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2030" />
              <XAxis dataKey="rpm" stroke="#666"
                label={{value:'RPM', position:'insideBottom', offset:-10, fill:'#666', fontSize:12}} />
              <YAxis yAxisId="l" stroke="#666"
                label={{value:'e³m³/d', angle:-90, position:'insideLeft', fill:'#666', fontSize:11}} />
              <YAxis yAxisId="r" orientation="right" stroke="#666"
                label={{value:'kW', angle:90, position:'insideRight', fill:'#666', fontSize:11}} />
              <Tooltip contentStyle={TOOLTIP_STYLE}
                formatter={(v,n) => n==='gasFlowVolE3m3d'
                  ? [`${v.toFixed(1)} e³m³/d`,'Flow']
                  : [`${v.toFixed(0)} kW`,'Power']} />
              <Legend wrapperStyle={{color:'#8b9ab3', fontSize:12}} />
              <Line yAxisId="l" dataKey="gasFlowVolE3m3d" name="Flow Rate" stroke="#4ecb71" dot={false} strokeWidth={2}/>
              <Line yAxisId="r" dataKey="totalPower"       name="Power"     stroke="#e06c75" dot={false} strokeWidth={2}/>
            </LineChart>
          </ResponsiveContainer>

          <ResponsiveContainer width="100%" height={220} style={{marginTop:12}}>
            <LineChart data={results.rpmResults}
              margin={{top:5, right:40, left:10, bottom:20}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2030" />
              <XAxis dataKey="rpm" stroke="#666"
                label={{value:'RPM', position:'insideBottom', offset:-10, fill:'#666', fontSize:12}} />
              <YAxis stroke="#666"
                label={{value:'kW·d/e³m³', angle:-90, position:'insideLeft', fill:'#666', fontSize:11}} />
              <Tooltip contentStyle={TOOLTIP_STYLE}
                formatter={v=>[`${v.toFixed(2)} kW/(e³m³/d)`,'Specific Power']} />
              <Legend wrapperStyle={{color:'#8b9ab3', fontSize:12}} />
              <Line dataKey="specificPower" name="Specific Power" stroke="#f0a500" dot={false} strokeWidth={2}/>
            </LineChart>
          </ResponsiveContainer>

          {/* T-s Diagram - HIDDEN (Code needs refinement for proper thermodynamic curves) */}
          {/*
          <h4 style={{marginTop:30}}>Thermodynamic T-s Diagram (Mid-RPM)</h4>
          <ResponsiveContainer width="100%" height={300}>
            ...
          </ResponsiveContainer>
          */}

          {/* P-V Diagram */}
          <h4 style={{marginTop:30}}>Indicator Cards (P-V Diagram, Mid-RPM)</h4>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{top:20, right:30, bottom:20, left:10}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1a2030" />
              <XAxis type="number" dataKey="V" name="Volume" unit=" L" stroke="#666" domain={['auto', 'auto']} />
              <YAxis type="number" dataKey="P" name="Pressure" unit=" bar" stroke="#666" domain={['auto', 'auto']} />
              <ZAxis range={[10, 10]} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={TOOLTIP_STYLE} />
              <Legend />
              {midResult.stages.map((st, i) => {
                const colors = ['#4ecb71', '#e06c75', '#61afef', '#c678dd'];
                return (
                  <Scatter key={i} name={`Stage ${i+1} P-V`} data={st.pvPath} fill={colors[i%colors.length]} line lineType="joint" shape={()=><rect width={0} height={0}/>} />
                );
              })}
            </ScatterChart>
          </ResponsiveContainer>

          {/* Stage details at mid-RPM */}
          {midResult && (
            <div style={{marginTop:20}}>
              <h4>Stage Detail at {midResult.rpm} RPM</h4>
              {midResult.stages.map((st, idx) => (
                <div key={idx} className="stage-detail-card">
                  <div className="stage-detail-header">Stage {st.stageNumber}</div>
                  <div className="two-col-grid" style={{gap:8}}>
                    <div className="kv"><span>Inlet P</span><span>{st.inletPressure.toFixed(2)} bar</span></div>
                    <div className="kv"><span>Discharge P</span><span>{st.dischargePressure.toFixed(2)} bar</span></div>
                    <div className="kv"><span>Compression Ratio</span><span>{st.compressionRatio.toFixed(3)}</span></div>
                    <div className="kv"><span>Inlet T</span><span>{st.inletTemperature.toFixed(1)} °C</span></div>
                    <div className="kv"><span>Isentropic Discharge T</span><span>{st.isentropicDischargeTemp.toFixed(1)} °C</span></div>
                    <div className="kv"><span>Actual Discharge T</span><span>{st.dischargeTemperature.toFixed(1)} °C</span></div>
                    <div className="kv"><span>Cooler Outlet T</span><span>{st.coolerOutletTemperature.toFixed(1)} °C</span></div>
                    <div className="kv"><span>Z (inlet)</span><span>{st.Z_inlet.toFixed(4)}</span></div>
                    <div className="kv"><span>γ (mixture, real-gas)</span><span>{st.gamma.toFixed(4)}</span></div>
                    <div className="kv"><span>Vol. Efficiency</span><span>{st.volumetricEfficiency.toFixed(1)}%</span></div>
                    <div className="kv"><span>Displacement</span><span>{st.displacement.toFixed(2)} L/s</span></div>
                    <div className="kv"><span>Actual Inlet Flow</span><span>{st.Q_act.toFixed(2)} L/s</span></div>
                    <div className="kv"><span>Mass Flow</span><span>{(st.massFlow*3600).toFixed(1)} kg/h</span></div>
                    <div className="kv"><span>Stage Power</span><span>{st.power.toFixed(1)} kW</span></div>
                    <div className="kv"><span>Cooling Duty</span><span>{st.coolingDuty.toFixed(1)} kW</span></div>
                    <div className="kv"><span>Vapour Fraction (cooler out)</span><span>{(st.vaporFraction*100).toFixed(1)}%</span></div>
                    <div className="kv"><span>Isentropic Work</span><span>{st.w_is.toFixed(2)} kJ/kg</span></div>
                  </div>

                  {st.vaporFraction < 0.995 && (
                    <div className="info-box warn" style={{marginTop:8, fontSize:12}}>
                      ⚠️ Liquid dropout at cooler: {((1-st.vaporFraction)*100).toFixed(1)}% liquid phase — check KO drum sizing.
                    </div>
                  )}

                  <div style={{display:'flex', gap:16, marginTop:8}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12, color:'#8b9ab3', marginBottom:4}}>Vapour comp (mol%)</div>
                      {Object.entries(st.vaporComposition).map(([n,x])=>(
                        <div key={n} className="kv" style={{fontSize:11}}>
                          <span>{n}</span><span>{(x*100).toFixed(2)}%</span>
                        </div>
                      ))}
                    </div>
                    {st.vaporFraction < 0.995 && (
                      <div style={{flex:1}}>
                        <div style={{fontSize:12, color:'#8b9ab3', marginBottom:4}}>Liquid comp (mol%)</div>
                        {Object.entries(st.liquidComposition).map(([n,x])=>(
                          <div key={n} className="kv" style={{fontSize:11}}>
                            <span>{n}</span><span>{(x*100).toFixed(2)}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Full RPM sweep table */}
          <h4 style={{marginTop:20}}>RPM Sweep Summary</h4>
          <div style={{overflowX:'auto'}}>
            <table className="stage-table">
              <thead>
                <tr>
                  <th>RPM</th>
                  <th>Flow (e³m³/d)</th>
                  <th>Brake Power (kW)</th>
                  <th>Spec. Power (kW·d/e³m³)</th>
                  <th>Cooling Duty (kW)</th>
                  {results.rpmResults[0]?.stages.map((_,i)=>(
                    <th key={i}>Stg {i+1} η_v (%)</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.rpmResults.map((r, i) => (
                  <tr key={i} style={i===midRPMidx?{background:'#1a2533'}:{}}>
                    <td>{r.rpm}</td>
                    <td>{r.gasFlowVolE3m3d.toFixed(1)}</td>
                    <td>{r.totalPower.toFixed(0)}</td>
                    <td>{r.specificPower.toFixed(2)}</td>
                    <td>{r.totalCooling.toFixed(0)}</td>
                    {r.stages.map((st,j)=>(
                      <td key={j}>{st.volumetricEfficiency.toFixed(1)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="info-box" style={{marginTop:16}}>
            <strong>Physics notes:</strong>
            <ul style={{margin:'6px 0 0 16px', fontSize:12}}>
              <li>Volumetric efficiency: η_v = 1 + c − c·(r·Z₁/Z₂)^(1/γ)  [API 618, with Z-ratio correction]</li>
              <li>Real-gas γ = Cp_mix / (Cp_mix − R·Z₁)  — uses mole-weighted mixture Cp, not pure methane</li>
              <li>Isentropic work: w_is = Cp_mass·(T₂ₛ−T₁)·Z₁  [Z₁ real-gas departure correction]</li>
              <li>Brake power: W = ṁ·w_is / (η_is·η_mech)</li>
              <li>Inter-stage flash: Wilson K-values + Rachford-Rice for liquid dropout prediction</li>
              <li>Standard flow: ṁ/ρ_std at 15°C, 101.325 kPa, ideal gas</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReciprocatingCompressorCalculator;
